import { cloneElement } from 'react';

import { Prominences, Sentiments } from '@sede-x/shell-ds-fabrics';

import { ColorEntitySentimentModes, Theme } from '../theme';
import { badgeConsts } from './Badge.consts';
import { BadgeSentiment, BadgeSize, BadgeVariant, IBaseBadgeProps } from './Badge.types';
import { Sizes, Variants } from '../consts';
import { useCustomTheme } from '../providers';

export const getColorByVariant = (theme: Theme, sentiment: BadgeSentiment, variant: BadgeVariant) => {
  switch (variant) {
    case Variants.Outlined:
      if (sentiment === Sentiments.Neutral) {
        return theme.text.onSurface.strong;
      }

      return theme.text.onSurface[sentiment];
    case Variants.Subtle:
      return theme.text.onSystem.subtle[sentiment];
    case Variants.Filled:
    default:
      return theme.text.onSystem.strong[sentiment];
  }
};

export const getBackgroundColorByVariant = (theme: Theme, sentiment: BadgeSentiment, variant: BadgeVariant): string => {
  switch (variant) {
    case Variants.Outlined:
      return Variants.Transparent;
    case Variants.Subtle:
      return theme.system.subtle[sentiment];
    case Variants.Filled:
    default:
      return theme.system.strong[sentiment];
  }
};

export const getBorderColorByVariant = (theme: Theme, sentiment: BadgeSentiment, variant: BadgeVariant): string => {
  switch (variant) {
    case Variants.Outlined:
      if (sentiment === Sentiments.Neutral) {
        return theme.border.medium;
      }

      if (sentiment === Sentiments.Warning) {
        return theme.system.strong[sentiment];
      }

      return theme.text.onSurface[sentiment];
    case Variants.Subtle:
      return theme.system.subtle[sentiment];
    case Variants.Filled:
    default:
      return theme.system.strong[sentiment];
  }
};

export const getBadgeFontStyles = (size: BadgeSize, theme: Theme) => {
  switch (size) {
    case Sizes.Large:
      return theme.label.large.bold;
    case Sizes.ExtraSmall || Sizes.Small:
      return theme.label.small.bold;
    case Sizes.ExtraSmall:
      // TODO: Ideally we should return an object here but can't due to
      // convertCSSFontToStylesheet. Investigate later.
      return 'line-height: 14px';
    case Sizes.Medium:
    default:
      return theme.label.medium.bold;
  }
};

export const getBadgeBorderStyles = (sentiment: BadgeSentiment, variant: BadgeVariant, theme: Theme) => {
  if (variant === Variants.Outlined) {
    return {
      borderColor: getBorderColorByVariant(theme, sentiment, variant),
      borderWidth: 1,
      borderStyle: 'solid',
    };
  }

  return null;
};

export type UseBadgePropsReturnType = ReturnType<typeof useBadgeProps>;

export const useBadgeProps = ({
  size = Sizes.Medium,
  variant = Variants.Filled,
  sentiment = Sentiments.Neutral,
  icon,
  children,
  round = !!icon && !children,
}: IBaseBadgeProps) => {
  const theme = useCustomTheme() as Theme;
  const backgroundColor = getBackgroundColorByVariant(theme, sentiment, variant);
  const color = getColorByVariant(theme, sentiment, variant) as string;
  const fontStyles = getBadgeFontStyles(size, theme);
  const borderStyles = getBadgeBorderStyles(sentiment, variant, theme);
  const iconComponent =
    icon && size !== Sizes.Micro
      ? cloneElement(icon, {
          fill: color,
          height: badgeConsts[size].icon,
          width: badgeConsts[size].icon,
        })
      : null;
  const shouldRenderChildren = !!children && size !== Sizes.Micro;

  return {
    round,
    children,
    shouldRenderChildren,
    iconComponent,
    size,
    variant,
    sentiment,
    backgroundColor,
    color,
    fontStyles,
    borderStyles,
  };
};

export const getBadgeProminence = (variant?: string) => {
  switch (variant) {
    case Variants.Filled:
      return Prominences.Strong;
    case Variants.Subtle:
      return Prominences.Subtle;
    case Variants.Outlined:
    default:
      return Prominences.Transparent;
  }
};

export const getBadgeEntityOverrides = (variant?: string) => {
  if (variant === Variants.Filled) {
    return undefined;
  }

  return {
    text: { entitySentimentMode: ColorEntitySentimentModes.colour },
    border: { entitySentimentMode: ColorEntitySentimentModes.colour },
  };
};
